import { type FC, useCallback, useState } from 'react';
import { useCheckoutContactSchema } from './use-otp-channel-selector-schema.hook';
import { Fragment, LoadingOverlay, Text, withDialog } from '@kidslabs/mario-nextjs-react-ui-core';
import { OtpChannelSelectorField } from '../forms-fields';
import { useAuthProvider } from '../../providers';
import { ErrorCodesEnum } from '../../enums';
import { useAuth } from '../../hooks';
import type { OtpChannelSelectorForm, OtpChannelSelectorProps } from './otp-channel-selector.types';
import { useTranslation } from 'next-i18next';

import styles from './otp-channel-selector.module.css';

const OtpChannelSelectorContent: FC<OtpChannelSelectorProps> = ({
    FormComponent,
    hideDialog,
    setDialogOptions,
    showDialog,
}) => {
    const { t } = useTranslation();
    const { requestValidatePhone } = useAuthProvider();
    const { setCurrentStep } = useAuth();

    const [isSubmit, setIsSubmit] = useState(false);

    const checkoutContactSchema = useCheckoutContactSchema();

    const handleShowDialog = useCallback(
        (message: string) => {
            setDialogOptions({
                title: t('text_notification', 'Thông báo'),
                content: message,
                acceptLabel: t('text_agree', 'Đồng ý'),
                loading: false,
                onAccept: () => {
                    setCurrentStep('EnterPhoneNumber');
                    hideDialog();
                },
            });
            showDialog();
        },
        [hideDialog, setCurrentStep, setDialogOptions, showDialog, t]
    );

    const onSubmit = useCallback(
        (data: OtpChannelSelectorForm) => {
            setIsSubmit(true);
            if (data?.otpChannel) {
                requestValidatePhone({ otpChannel: data?.otpChannel })
                    .then(result => {
                        if (result.errorCode === ErrorCodesEnum.SUCCESS) {
                            setCurrentStep('VerifyOtp');
                        } else {
                            handleShowDialog(result.message);
                        }
                    })
                    .catch(e => {
                        handleShowDialog(e?.message ?? t('text_network_request-failed'));
                    })
                    .finally(() => {
                        setIsSubmit(false);
                    });
            }
        },
        [t, handleShowDialog, requestValidatePhone, setCurrentStep]
    );

    return (
        <Fragment>
            <FormComponent<OtpChannelSelectorForm> resolver={checkoutContactSchema} onSubmit={onSubmit}>
                {({ control, formState: { errors } }) => {
                    return (
                        <Fragment>
                            <OtpChannelSelectorField name={'otpChannel'} control={control} />
                            <Text showIf={!!errors} className={styles.error}>
                                {errors?.otpChannel?.message}
                            </Text>
                        </Fragment>
                    );
                }}
            </FormComponent>
            <LoadingOverlay
                showIf={isSubmit}
                className={styles.loadingOverlay}
                message={t('text_verifying_phone_number', 'Đang xác thực số điện thoại của bạn')}
            />
        </Fragment>
    );
};

const OtpChannelSelectorInner: FC<OtpChannelSelectorProps> = props => {
    return <OtpChannelSelectorContent {...props} />;
};

export const OtpChannelSelector = withDialog(OtpChannelSelectorInner);
